import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  ContentCopyOutlined,
  LocalOfferOutlined,
  PauseCircleOutline,
  PlayCircleOutline,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  createPromotion,
  getBillingProductCatalog,
  getPromotions,
  updatePromotionStatus,
} from '../services/api';

const PRODUCTS = [
  { key: 'atendabot', label: 'AtendaBot', priceCents: 39000 },
  { key: 'reservo', label: 'Reservo', priceCents: 14900 },
  { key: 'one_menu', label: 'One Menu', priceCents: 12900 },
  { key: 'feedback_hero', label: 'Feedback Hero', priceCents: 8900 },
  { key: 'curriculo_certo', label: 'Currículo Certo', priceCents: 4900 },
];

const STATUS_LABELS = {
  draft: 'Rascunho',
  active: 'Ativa',
  paused: 'Pausada',
  expired: 'Expirada',
};

const STATUS_COLORS = {
  draft: 'default',
  active: 'success',
  paused: 'warning',
  expired: 'error',
};

const initialForm = (products = PRODUCTS) => ({
  name: '',
  code: '',
  status: 'active',
  pricingMode: 'fixed_price',
  percentOff: 20,
  startsAt: '',
  endsAt: '',
  maxRedemptions: '',
  firstPurchaseOnly: true,
  requiresFullBundle: false,
  selectedProducts: [],
  prices: Object.fromEntries(products.map((product) => [
    product.key,
    (product.priceCents / 100).toFixed(2),
  ])),
});

const formatMoney = (cents) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
}).format((Number(cents) || 0) / 100);

const buildSignupLink = (promotion) => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  return `${baseUrl}/cadastro?oferta=${encodeURIComponent(promotion.public_token)}`;
};

const PromotionFormDialog = ({ open, onClose, onCreated, products = PRODUCTS }) => {
  const [form, setForm] = useState(() => initialForm(products));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initialForm(products));
  }, [open, products]);

  const toggleProduct = (productKey) => {
    setForm((current) => ({
      ...current,
      selectedProducts: current.selectedProducts.includes(productKey)
        ? current.selectedProducts.filter((key) => key !== productKey)
        : [...current.selectedProducts, productKey],
    }));
  };

  const handleSubmit = async () => {
    if (form.name.trim().length < 3 || form.code.trim().length < 3) {
      toast.error('Informe nome e código com pelo menos 3 caracteres.');
      return;
    }
    if (!form.selectedProducts.length) {
      toast.error('Selecione ao menos um produto.');
      return;
    }

    const products = form.selectedProducts.map((productKey) => ({
      product_key: productKey,
      ...(form.pricingMode === 'fixed_price'
        ? {
            promotional_amount_cents:
              productKey === 'one_menu' && form.selectedProducts.includes('atendabot')
                ? 0
                : Math.round(Number(form.prices[productKey]) * 100),
          }
        : {}),
    }));
    if (products.some((product) => (
      form.pricingMode === 'fixed_price'
      && (!Number.isInteger(product.promotional_amount_cents) || product.promotional_amount_cents < 0)
    ))) {
      toast.error('Revise os preços promocionais.');
      return;
    }

    setSaving(true);
    try {
      const promotion = await createPromotion({
        name: form.name.trim(),
        code: form.code.trim(),
        status: form.status,
        pricing_mode: form.pricingMode,
        percent_off: form.pricingMode === 'percent_off' ? Number(form.percentOff) : null,
        starts_at: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        ends_at: form.endsAt ? new Date(form.endsAt).toISOString() : null,
        max_redemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
        first_purchase_only: form.firstPurchaseOnly,
        requires_full_bundle: form.requiresFullBundle,
        products,
      });
      toast.success('Promoção criada.');
      onCreated(promotion);
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Não foi possível criar a promoção.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Nova promoção</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.25} sx={{ pt: 0.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Nome da campanha"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              fullWidth
            />
            <TextField
              label="Código promocional"
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
              helperText="Identificação comercial da oferta"
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Modelo de preço</InputLabel>
              <Select
                value={form.pricingMode}
                label="Modelo de preço"
                onChange={(event) => setForm({ ...form, pricingMode: event.target.value })}
              >
                <MenuItem value="fixed_price">Preço fixo por produto</MenuItem>
                <MenuItem value="percent_off">Mesmo percentual nos produtos</MenuItem>
              </Select>
            </FormControl>
            {form.pricingMode === 'percent_off' && (
              <TextField
                label="Desconto"
                type="number"
                value={form.percentOff}
                onChange={(event) => setForm({ ...form, percentOff: event.target.value })}
                inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                fullWidth
              />
            )}
          </Stack>

          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Produtos da oferta</Typography>
            <Stack spacing={1}>
              {products.map((product) => {
                const selected = form.selectedProducts.includes(product.key);
                const includedByAtendabot =
                  product.key === 'one_menu' && form.selectedProducts.includes('atendabot');
                return (
                  <Paper key={product.key} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                      <FormControlLabel
                        sx={{ flex: 1, m: 0 }}
                        control={<Checkbox checked={selected} onChange={() => toggleProduct(product.key)} />}
                        label={`${product.label} — ${formatMoney(product.priceCents)}/mês`}
                      />
                      {selected && form.pricingMode === 'fixed_price' && !includedByAtendabot && (
                        <TextField
                          label="Preço promocional"
                          size="small"
                          type="number"
                          value={form.prices[product.key]}
                          onChange={(event) => setForm({
                            ...form,
                            prices: { ...form.prices, [product.key]: event.target.value },
                          })}
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                          sx={{ width: { sm: 210 } }}
                        />
                      )}
                      {selected && includedByAtendabot && (
                        <Chip size="small" color="success" label="Incluso no AtendaBot" />
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Início"
              type="datetime-local"
              value={form.startsAt}
              onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Término"
              type="datetime-local"
              value={form.endsAt}
              onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Limite de resgates"
              type="number"
              value={form.maxRedemptions}
              onChange={(event) => setForm({ ...form, maxRedemptions: event.target.value })}
              inputProps={{ min: 1, step: 1 }}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.firstPurchaseOnly}
                  onChange={(event) => setForm({ ...form, firstPurchaseOnly: event.target.checked })}
                />
              )}
              label="Apenas clientes sem assinatura anterior"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.requiresFullBundle}
                  onChange={(event) => setForm({ ...form, requiresFullBundle: event.target.checked })}
                />
              )}
              label="Remover um produto encerra os preços promocionais do pacote"
            />
          </Stack>
          <Alert severity="info">
            Os produtos desta campanha preservam o preço promocional. Outros produtos usam o
            desconto progressivo calculado pela quantidade total do pacote.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Criando...' : 'Criar promoção'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PromotionsPage = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPromotions, setTotalPromotions] = useState(0);
  const [catalogProducts, setCatalogProducts] = useState(PRODUCTS);
  const [catalogAvailable, setCatalogAvailable] = useState(false);

  const sortedPromotions = useMemo(
    () => [...promotions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [promotions]
  );

  const loadPromotions = useCallback(async (requestedPage, status) => {
    setLoading(true);
    try {
      const data = await getPromotions({ page: requestedPage, pageSize: 10, status });
      const items = Array.isArray(data) ? data : data?.items;
      setPromotions(Array.isArray(items) ? items : []);
      setTotalPages(Array.isArray(data) ? (data.length ? 1 : 0) : Number(data?.totalPages) || 0);
      setTotalPromotions(Array.isArray(data) ? data.length : Number(data?.total) || 0);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao carregar promoções.');
      setPromotions([]);
      setTotalPages(0);
      setTotalPromotions(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    getBillingProductCatalog()
      .then((data) => {
        if (!active || !Array.isArray(data)) return;
        const pricesByKey = Object.fromEntries(
          data.map((product) => [product.key, Number(product.priceCents)])
        );
        const hasEveryPrice = PRODUCTS.every(
          (product) => Number.isInteger(pricesByKey[product.key])
            && pricesByKey[product.key] >= 0
        );
        if (!hasEveryPrice) {
          throw new Error('Catálogo de produtos incompleto');
        }
        setCatalogProducts(PRODUCTS.map((product) => ({
          ...product,
          priceCents: pricesByKey[product.key],
        })));
        setCatalogAvailable(true);
      })
      .catch(() => {
        if (!active) return;
        setCatalogAvailable(false);
        toast.error('Não foi possível carregar os preços atuais da Stripe.');
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    loadPromotions(page, statusFilter);
  }, [loadPromotions, page, statusFilter]);

  const copyLink = async (promotion) => {
    const link = buildSignupLink(promotion);
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link promocional copiado.');
    } catch (_error) {
      window.prompt('Copie o link promocional:', link);
    }
  };

  const toggleStatus = async (promotion) => {
    const status = promotion.status === 'active' ? 'paused' : 'active';
    try {
      await updatePromotionStatus(promotion.id, status);
      const nextPage = promotions.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        await loadPromotions(nextPage, statusFilter);
      }
      toast.success(status === 'active' ? 'Promoção ativada.' : 'Promoção pausada.');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao atualizar promoção.');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, width: '100%', mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalOfferOutlined color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Promoções</Typography>
          </Stack>
          <Typography color="text.secondary" mt={0.5}>
            Crie pacotes promocionais e links especiais para novos cadastros.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
          disabled={!catalogAvailable}
        >
          Nova promoção
        </Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        mb={2}
      >
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="promotion-status-filter-label">Status</InputLabel>
          <Select
            labelId="promotion-status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="active">Ativas</MenuItem>
            <MenuItem value="paused">Pausadas</MenuItem>
            <MenuItem value="expired">Expiradas</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {totalPromotions} {totalPromotions === 1 ? 'promoção' : 'promoções'}
        </Typography>
      </Stack>

      {!catalogAvailable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Os preços atuais da Stripe não puderam ser carregados. Atualize a página antes de criar
          uma promoção.
        </Alert>
      )}

      {loading && <Typography color="text.secondary">Carregando...</Typography>}
      {!loading && sortedPromotions.length === 0 && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700 }}>
            Nenhuma promoção {STATUS_LABELS[statusFilter]?.toLowerCase()} encontrada.
          </Typography>
          {statusFilter === 'active' && (
            <Typography color="text.secondary">
              Crie uma campanha para gerar um link de cadastro.
            </Typography>
          )}
        </Paper>
      )}

      <Stack spacing={2}>
        {sortedPromotions.map((promotion) => (
          <Paper key={promotion.id} variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{promotion.name}</Typography>
                  <Chip
                    size="small"
                    label={STATUS_LABELS[promotion.status] || promotion.status}
                    color={STATUS_COLORS[promotion.status] || 'default'}
                  />
                  <Chip size="small" variant="outlined" label={promotion.code} />
                </Stack>
                <Stack direction="row" spacing={0.75} mt={1.25} flexWrap="wrap" useFlexGap>
                  {(promotion.products || []).map((product) => {
                    const catalog = catalogProducts.find(
                      (entry) => entry.key === product.product_key
                    );
                    const includedByAtendabot = product.product_key === 'one_menu'
                      && promotion.products.some((entry) => entry.product_key === 'atendabot');
                    const detail = includedByAtendabot
                      ? 'Incluso'
                      : promotion.pricing_mode === 'fixed_price'
                      ? formatMoney(product.promotional_amount_cents)
                      : `${promotion.percent_off}% off`;
                    return (
                      <Chip
                        key={product.product_key}
                        size="small"
                        label={`${catalog?.label || product.product_key}: ${detail}`}
                        sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 600 }}
                      />
                    );
                  })}
                </Stack>
                <Typography variant="body2" color="text.secondary" mt={1.25} sx={{ wordBreak: 'break-all' }}>
                  {buildSignupLink(promotion)}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} alignItems="stretch">
                <Button size="small" variant="outlined" startIcon={<ContentCopyOutlined />} onClick={() => copyLink(promotion)}>
                  Copiar link
                </Button>
                <Button
                  size="small"
                  color={promotion.status === 'active' ? 'warning' : 'success'}
                  startIcon={promotion.status === 'active' ? <PauseCircleOutline /> : <PlayCircleOutline />}
                  onClick={() => toggleStatus(promotion)}
                  disabled={promotion.status === 'expired'}
                >
                  {promotion.status === 'active' ? 'Pausar' : 'Ativar'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {!loading && totalPages > 1 && (
        <Stack alignItems="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            color="primary"
            onChange={(_event, nextPage) => setPage(nextPage)}
          />
        </Stack>
      )}

      <PromotionFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        products={catalogProducts}
        onCreated={async () => {
          setStatusFilter('active');
          if (page !== 1) {
            setPage(1);
          } else {
            await loadPromotions(1, 'active');
          }
        }}
      />
    </Box>
  );
};

export default PromotionsPage;
